import React, { FC } from "react";
import { Box, Typography } from "@mui/material";

interface DonutChartData {
  label: string;
  value: number;
  color: string;
}

interface DonutChartProps {
  data: DonutChartData[];
  title?: string;
  size?: number;
  strokeWidth?: number;
}

const DonutChart: FC<DonutChartProps> = ({
  data,
  title,
  size = 200,
  strokeWidth = 20,
}) => {
  const radius = (size - strokeWidth) / 2;
  // const circumference = radius * 2 * Math.PI;
  const centerX = size / 2;
  const centerY = size / 2;

  let cumulativePercentage = 0;

  const createArcPath = (
    startPercentage: number,
    endPercentage: number,
    radius: number,
  ): string => {
    const startAngle = (startPercentage / 100) * 2 * Math.PI - Math.PI / 2;
    const endAngle = (endPercentage / 100) * 2 * Math.PI - Math.PI / 2;

    const startX = centerX + radius * Math.cos(startAngle);
    const startY = centerY + radius * Math.sin(startAngle);
    const endX = centerX + radius * Math.cos(endAngle);
    const endY = centerY + radius * Math.sin(endAngle);

    const largeArcFlag = endPercentage - startPercentage > 50 ? 1 : 0;

    return `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`;
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      <Box position="relative" display="inline-block">
        <svg width={size} height={size}>
          {/* Background circle */}
          <circle
            cx={centerX}
            cy={centerY}
            r={radius}
            fill="none"
            stroke="#e0e0e0"
            strokeWidth={strokeWidth}
          />
          {/* Data segments */}
          {data.map((segment, index) => {
            const startPercentage = cumulativePercentage;

            const endPercentage = cumulativePercentage + segment.value;

            cumulativePercentage = endPercentage;

            return (
              <path
                key={index}
                d={createArcPath(startPercentage, endPercentage, radius)}
                fill="none"
                stroke={segment.color}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
              />
            );
          })}
        </svg>
        {/* Center text */}
        {title && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            sx={{
              transform: "translate(-50%, -50%)",
              textAlign: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              {title}
            </Typography>
          </Box>
        )}
      </Box>
      {/* Legend */}
      <Box display="flex" flexDirection="column" gap={1} mt={1}>
        {data.map((segment, index) => (
          <Box key={index} display="flex" alignItems="center" gap={1}>
            <Box width={12} height={12} bgcolor={segment.color} borderRadius="50%" />
            <Typography variant="caption">{segment.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default DonutChart;
